import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SyntheticMdbLoader({ accessToken, onLoaded }) {
  const [capability, setCapability] = useState(null),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [result, setResult] = useState(null);
  const lock = useRef(false),
    generation = useRef(0);
  useEffect(() => {
    let active = true;
    generation.current++;
    setCapability(null);
    setConfirmed(false);
    setResult(null);
    setBusy(false);
    setError("");
    lock.current = false;
    if (process.env.NODE_ENV !== "production" && accessToken)
      apiRequest("/biometric-devices/development/mdb-preview", { accessToken })
        .then((r) => {
          if (active) setCapability(r);
        })
        .catch(() => {
          if (active) setCapability({ enabled: false });
        });
    return () => {
      active = false;
      generation.current++;
    };
  }, [accessToken]);
  async function load() {
    if (lock.current || !confirmed || !capability?.enabled) return;
    const ticket = generation.current;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await apiRequest("/biometric-devices/development/mdb-preview", {
        method: "POST",
        accessToken,
        body: { retainSyntheticPreview: true },
      });
      if (ticket !== generation.current) return;
      setResult(r);
      await onLoaded(r);
    } catch (e) {
      if (ticket === generation.current)
        setError(e.message || "Unable to retain the synthetic preview");
    } finally {
      if (ticket === generation.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  }
  if (process.env.NODE_ENV === "production") return null;
  return (
    <section className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <h2 className="font-bold text-amber-950">
        Development MDB test — retained preview
      </h2>
      <p className="text-sm text-amber-900">
        Loads 228 synthetic punches into a separate DEV ONLY device. No employee
        links, live attendance, salary or payroll are created. Repeating the
        load deduplicates the same source rows.
      </p>
      {capability?.enabled ? (
        <>
          <label className="flex gap-3 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I want to retain synthetic connector-preview records in this
            development database only.
          </label>
          <button
            type="button"
            disabled={busy || !confirmed}
            onClick={load}
            className="rounded-xl bg-amber-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-40"
          >
            {busy ? "Loading synthetic preview…" : "Load synthetic MDB preview"}
          </button>
        </>
      ) : (
        <p className="text-sm text-amber-900">
          Loader disabled. Restart the updated backend with{" "}
          <code>
            NODE_ENV=development HRMS_ENABLE_SYNTHETIC_PREVIEW=true npm run
            start:dev
          </code>
          . Only a loopback PostgreSQL database is accepted. Do not start a
          second backend on the same port.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-800">
          {error}
        </p>
      )}
      {result && (
        <p role="status" className="text-sm text-green-900">
          Retained preview ready: {result.inserted} new, {result.duplicates}{" "}
          already present. Select DEV ONLY — Synthetic MDB TD, choose machine ID
          1 and 20 October 2026, then view the preview.
        </p>
      )}
    </section>
  );
}
